import { Navigate } from 'react-router'
import { usePermissions } from '@/hooks/usePermissions'
import { PermissionEntityType } from '@/api/model'
import { useAuthStore } from '@/store/auth.store'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import { NoAccessNotice } from './NoAccessNotice'

/**
 * `/` landing. Every branch must end somewhere the user may actually go — redirecting to a
 * guarded route the user cannot open sends them straight back here and the app ping-pongs
 * between the two routes until React aborts with "Maximum update depth exceeded".
 *
 * An administrator holds the same company-wide permissions as a company management user, but
 * they only mean something once he has picked a customer portal in the header — the
 * company-scoped pages read the CompanyId claim and answer 400 without it. So he goes to a super
 * admin page until then, and follows the normal branches afterwards.
 *
 * Which super admin page depends on what he may open: those pages are permission gated too, and
 * sending him to one he cannot read would land him on a page full of 403s.
 */
export function LandingRoute() {
  const { userHasPermission, userHasAnyPermission } = usePermissions()
  const role = useAuthStore((s) => s.role)
  const companyId = useAuthStore((s) => s.companyId)

  if (role === 'Administrator' && companyId == null) {
    if (userHasPermission(PermissionEntityType.AdministratorGeneral, 'Billing')) {
      return <Navigate to="/super-admin/billing" replace />
    }

    if (userHasPermission(PermissionEntityType.AdministratorPermissions, 'View')) {
      return <Navigate to="/super-admin/permissions" replace />
    }

    if (
      userHasAnyPermission(PermissionEntityType.AdministratorCompanies, 'View') ||
      userHasPermission(PermissionEntityType.AdministratorCompanies, 'Create')
    ) {
      return <Navigate to="/super-admin/companies" replace />
    }

    return <NoAccessNotice />
  }

  if (userHasPermission(PermissionEntityType.General, 'Dashboard')) {
    return <DashboardPage />
  }

  if (userHasPermission(PermissionEntityType.General, 'AccountManagement')) {
    return <Navigate to="/employees" replace />
  }

  return <NoAccessNotice />
}
